// @workflow assistant.ui-tools
/**
 * Argument checks for the 3D viewer tools. Each returns the clean input, or null for
 * `unsupported`: only truly malformed arguments are refused (no ids or too many, a bad id, a bad
 * hex, a bad facility id, a tag in two colour groups). A label never fails a call; a bad one
 * falls back to the hex.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { VIEWER_LIMITS, FOCUS_MODE, GHOSTING_STYLE, PANE_TARGET } from './viewerContract.js';
import { cleanLabel, legendValues, normalizeHex } from './viewerColors.js';

/** Route-safe facility ids (GUIDs and the like): no slashes, dots or spaces. */
const FACILITY_ID = /^[\w-]+$/;

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isAbsent = (value) => value === undefined || value === null;

/** A trimmed id of at most `maxLength` characters, or null. */
function cleanId(value, maxLength) {
  if (typeof value !== 'string') return null;
  const id = value.trim();
  return id && id.length <= maxLength ? id : null;
}

/**
 * 1 to `maxCount` ids, trimmed, de-duplicated (ids compare without case) and in the order given;
 * null if the list is empty, too long, or any id is bad. Shared by the tag and task tools.
 */
export function cleanIdList(list, { maxCount, maxLength }) {
  if (!Array.isArray(list) || !list.length || list.length > maxCount) return null;
  const seen = new Set();
  const clean = [];
  for (const value of list) {
    const id = cleanId(value, maxLength);
    if (id === null) return null;
    const key = id.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      clean.push(id);
    }
  }
  return clean;
}

const cleanTagId = (value) => cleanId(value, VIEWER_LIMITS.MAX_TAG_ID_LENGTH);
const cleanTagIds = (list) => cleanIdList(list, { maxCount: VIEWER_LIMITS.MAX_TAG_IDS, maxLength: VIEWER_LIMITS.MAX_TAG_ID_LENGTH });

/**
 * The optional facility to open first: absent → null ("the facility on screen"), a clean id, or
 * undefined when it is malformed (so the call is refused).
 */
export function cleanFacilityId(value) {
  if (isAbsent(value)) return null;
  if (typeof value !== 'string') return undefined;
  const id = value.trim();
  return id.length <= VIEWER_LIMITS.MAX_FACILITY_ID_LENGTH && FACILITY_ID.test(id) ? id : undefined;
}

/** The optional pane target: absent → PANE_TARGET.NEW, a known value, or undefined (refused). */
function cleanPaneTarget(value) {
  if (isAbsent(value)) return PANE_TARGET.NEW;
  return Object.values(PANE_TARGET).includes(value) ? value : undefined;
}

/** The optional focus mode: absent → FOCUS_MODE.ZOOM, a known value, or undefined (refused). */
function cleanFocusMode(value) {
  if (isAbsent(value)) return FOCUS_MODE.ZOOM;
  return Object.values(FOCUS_MODE).includes(value) ? value : undefined;
}

/**
 * `{ tagIds, mode?, facilityId?, target? }` → `{ tagIds, mode, facilityId, target }` or null. A
 * missing mode is zoom, an unknown one is refused (as aiva.api ViewerToolArguments.Focus).
 */
export function checkFocusArgs(args) {
  if (!isPlainObject(args)) return null;
  const mode = cleanFocusMode(args.mode);
  const tagIds = cleanTagIds(args.tagIds);
  const facilityId = cleanFacilityId(args.facilityId);
  const target = cleanPaneTarget(args.target);
  return mode && tagIds && facilityId !== undefined && target !== undefined ? { tagIds, mode, facilityId, target } : null;
}

function checkGroup(group) {
  if (!isPlainObject(group)) return null;
  const color = normalizeHex(group.color);
  const tagIds = cleanTagIds(group.tagIds);
  return color && tagIds ? { tagIds, color, label: cleanLabel(group.label) } : null;
}

/**
 * `{ groups: [{ tagIds, color, label? }], facilityId? }` → `{ groups: [{ tagIds, color, label, value }], facilityId }`
 * or null. `value` is the group's legend value. A tag in two groups would be painted the "mixed"
 * colour, so the model has to pick one.
 */
export function checkColorArgs(args) {
  const groups = isPlainObject(args) ? args.groups : null;
  if (!Array.isArray(groups) || !groups.length || groups.length > VIEWER_LIMITS.MAX_COLOR_GROUPS) return null;
  const clean = groups.map(checkGroup);
  if (clean.includes(null)) return null;
  const keys = clean.flatMap((group) => group.tagIds.map((id) => id.toLowerCase()));
  if (keys.length > VIEWER_LIMITS.MAX_TAG_IDS || new Set(keys).size !== keys.length) return null;
  const facilityId = cleanFacilityId(args.facilityId);
  if (facilityId === undefined) return null;
  const values = legendValues(clean);
  return { groups: clean.map((group, i) => ({ ...group, value: values[i] })), facilityId };
}

/** `{ tagId, facilityId? }` → `{ tagId, facilityId }` or null: one tag (the tag tools, tagTools.js). */
export function checkTagArgs(args) {
  if (!isPlainObject(args)) return null;
  const tagId = cleanTagId(args.tagId);
  const facilityId = cleanFacilityId(args.facilityId);
  return tagId !== null && facilityId !== undefined ? { tagId, facilityId } : null;
}

export const checkNoArgs = (args) => (isAbsent(args) || isPlainObject(args) ? {} : null);

/**
 * set_ghosting: `{ on, style? }` → `{ on, style, facilityId: null }` or null. `on` must be a boolean;
 * `style` absent or null keeps the viewer's current style. Ghost mode acts on the viewer on screen.
 */
export function checkGhostingArgs(args) {
  if (!isPlainObject(args) || typeof args.on !== 'boolean') return null;
  if (!isAbsent(args.style) && !Object.values(GHOSTING_STYLE).includes(args.style)) return null;
  return { on: args.on, style: isAbsent(args.style) ? null : args.style, facilityId: null };
}
